import { DateTime } from '@ksyos/date-time';
import { Box, Button, FormErrorMessage, FormGroup, FormParagraph, IconButton, TextInput } from '@ksyos/design-system';
import { get } from 'lodash';
import { Fragment } from 'react';
import type { AgbCode } from '@common/interfaces/agbCode';
import type { OrganizationDataError } from '@common/interfaces/api/organizationDataErrorsApi';
import type { ValidationErrors } from '@frontend/utils';
import { formatString } from '@common/utils/formatString';
import { nonNullable } from '@common/utils/nonNullableTypeUtils';
import OrganizationAgbCodeDates from '@frontend/components/pages/organizations/organizationDetails/OrganizationAgbCodeDates';
import { getSelectedLanguage } from '@frontend/translate';

export type HandleChangeForIndex = <K extends keyof AgbCode>(index: number, name: K, value: AgbCode[K]) => void;

export function OrganizationAgbCodes({
    agbCodes,
    dataErrors,
    errors,
    isEditing,
    isRequired,
    onChange,
    onRemoveValidationErrors,
}: Readonly<{
    agbCodes?: AgbCode[];
    dataErrors: OrganizationDataError[];
    errors: ValidationErrors;
    isEditing: boolean;
    isRequired: boolean;
    onChange: (agbCodes: AgbCode[]) => void;
    onRemoveValidationErrors: (keys: string[]) => void;
}>) {
    const translate = getSelectedLanguage();

    return (
        <>
            {isEditing && (
                <>
                    <FormGroup
                        hasErrors={errors.some((key) => key?.startsWith('agbCodes') === true)}
                        isRequired={isRequired}
                        label={translate.organization.agbCodes}
                        sx={{ mb: '0 !important' }}
                    >
                        <>
                            {agbCodes?.map(({ agbCode, endDate, startDate }, index) => (
                                <Fragment key={`agb-${index}`}>
                                    <Box
                                        display="flex"
                                        gap={2}
                                        key={`agb-${index}`}
                                    >
                                        <Box minWidth={100}>
                                            <TextInput
                                                onChange={(e) => handleChangeForIndex(index, 'agbCode', e.target.value)}
                                                value={agbCode}
                                            />
                                        </Box>
                                        <OrganizationAgbCodeDates
                                            endDate={endDate}
                                            handleChangeForIndex={handleChangeForIndex}
                                            index={index}
                                            startDate={startDate}
                                        />

                                        <IconButton
                                            icon="delete"
                                            onClick={() => deleteAgbCodeForIndex(index)}
                                        />
                                    </Box>
                                    <Box mb={4}>
                                        {hasAgbCodeErrorForIndex(index) && (
                                            <FormErrorMessage>{getAgbCodeErrorForIndex(index)}</FormErrorMessage>
                                        )}
                                    </Box>
                                </Fragment>
                            ))}
                            {errors.has('agbCodes') && (
                                <FormErrorMessage sx={{ mt: 0 }}>
                                    {formatErrorMessage(nonNullable(errors.get('agbCodes')))}
                                </FormErrorMessage>
                            )}
                        </>
                    </FormGroup>
                    <Button
                        iconLeft="add"
                        onClick={addAgbCode}
                        sx={{ mb: 4 }}
                        variant="link"
                    >
                        {translate.organization.addAgbCode}
                    </Button>
                </>
            )}

            {!isEditing && (
                <FormGroup label={translate.organization.agbCodes}>
                    {agbCodes?.map((agbCode, index) => (
                        <FormParagraph key={`agb-${index}`}>{formatAgbCode(agbCode)}</FormParagraph>
                    ))}
                </FormGroup>
            )}
        </>
    );

    function formatErrorMessage(validationError: string) {
        const errorMessage = get(translate.validation, validationError);

        switch (validationError) {
            case 'agbStartDateMismatch': {
                const vektisValue = nonNullable(
                    dataErrors.find((error) => error.type === 'agbStartDateMismatch')?.details.vektis
                );

                return formatString(errorMessage, [DateTime.from(vektisValue).format('DD-MM-YYYY')]);
            }

            default:
                return errorMessage;
        }
    }

    function addAgbCode() {
        const newAgbCodes = [...(agbCodes ?? [])];

        newAgbCodes.push({ agbCode: '', endDate: null, startDate: DateTime.today().format('YYYY-MM-DD') });

        onChange(newAgbCodes);
    }

    function handleChangeForIndex<K extends keyof AgbCode>(index: number, name: K, value: AgbCode[K]) {
        const newAgbCodes = [...(agbCodes ?? [])];
        const agbCode = newAgbCodes[index] ?? {};

        newAgbCodes[index] = {
            ...agbCode,
            [name]: value,
        };

        onChange(newAgbCodes);
    }

    function hasAgbCodeErrorForIndex(index: number) {
        return errors.keySeq().some((key) => key?.startsWith(`agbCodes.${index}.`) === true);
    }

    function getAgbCodeErrorForIndex(index: number) {
        return get(
            translate.validation,
            nonNullable(errors.get(errors.keySeq().find((key) => key?.startsWith(`agbCodes.${index}.`) === true)))
        );
    }

    function formatAgbCode({ agbCode, endDate, startDate }: AgbCode) {
        return `${agbCode} (${translate.common.from} ${DateTime.from(startDate).format('DD-MM-YYYY')}${
            endDate ? ` ${translate.common.until} ${DateTime.from(endDate).format('DD-MM-YYYY')}` : ''
        })`;
    }

    function deleteAgbCodeForIndex(index: number) {
        const newAgbCodes = [...(agbCodes ?? [])];
        newAgbCodes.splice(index, 1);

        onChange(newAgbCodes);
        removeErrorsForIndex(index);
    }

    function removeErrorsForIndex(index: number) {
        const errorKeysForIndex = errors
            .keySeq()
            .toArray()
            .filter((key) => key?.startsWith(`agbCodes.${index}.`) === true);

        if (errorKeysForIndex.length > 0) {
            onRemoveValidationErrors(errorKeysForIndex);
        }
    }
}
